import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle2, Loader2 } from "lucide-react";

const PLAN_OPTIONS = [
  { v: "Core Accountability", l: "RetainProof Core Accountability" },
  { v: "Performance Intelligence", l: "RetainProof Performance Intelligence" },
  { v: "Executive Oversight", l: "RetainProof Executive Oversight" },
  { v: "Accountability Assessment", l: "Marketing Accountability Assessment" },
  { v: "Not sure yet", l: "Not sure yet — help me decide" },
];

const EMPTY = {
  firm_name: "", name: "", email: "", phone: "", offices: "", vendor_count: "",
  monthly_spend: "", case_system: "", call_tracking: "", reporting_challenges: "",
  preferred_plan: "", consultation_date: "",
};

export function ConsultationForm({ open, onClose, requestType = "general_consultation", preferredPlan = "", title }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (open) {
      setDone(false);
      setForm({ ...EMPTY, preferred_plan: preferredPlan });
    }
  }, [open, preferredPlan]);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/public/consultation", { ...form, request_type: requestType, offices: form.offices });
      setDone(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="consultation-dialog">
        {done ? (
          <div className="text-center py-6" data-testid="consultation-success">
            <CheckCircle2 className="h-14 w-14 mx-auto text-[#156F52]" />
            <h3 className="font-display text-2xl mt-4">Request received</h3>
            <p className="text-sm text-zinc-600 mt-2">
              A RetainProof specialist will reach out to schedule your session and confirm next steps.
            </p>
            <Button onClick={onClose} className="mt-6 rounded-none bg-[#0B1F33] text-white" data-testid="consultation-close">
              Done
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">{title || "Request a consultation"}</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-zinc-500 -mt-1">
              Onboarding requires firm-specific data configuration — this is a guided setup, not an instant self-service sign-up. Tell us about your firm and we'll take it from there.
            </p>
            <form onSubmit={submit} className="space-y-3 mt-2">
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Firm name" tid="c-firm"><Input required value={form.firm_name} onChange={upd("firm_name")} className="rounded-none" data-testid="c-firm-input" /></Field>
                <Field label="Contact name" tid="c-name"><Input required value={form.name} onChange={upd("name")} className="rounded-none" data-testid="c-name-input" /></Field>
                <Field label="Email" tid="c-email"><Input required type="email" value={form.email} onChange={upd("email")} className="rounded-none" data-testid="c-email-input" /></Field>
                <Field label="Phone" tid="c-phone"><Input value={form.phone} onChange={upd("phone")} className="rounded-none" data-testid="c-phone-input" /></Field>
                <Field label="Number of offices" tid="c-offices"><Input value={form.offices} onChange={upd("offices")} className="rounded-none" data-testid="c-offices-input" /></Field>
                <Field label="Number of marketing vendors" tid="c-vendors"><Input value={form.vendor_count} onChange={upd("vendor_count")} className="rounded-none" data-testid="c-vendors-input" /></Field>
                <Field label="Approx. monthly marketing spend" tid="c-spend"><Input value={form.monthly_spend} onChange={upd("monthly_spend")} placeholder="$" className="rounded-none" data-testid="c-spend-input" /></Field>
                <Field label="Case-management system" tid="c-case"><Input value={form.case_system} onChange={upd("case_system")} placeholder="Clio, MyCase…" className="rounded-none" data-testid="c-case-input" /></Field>
                <Field label="Call-tracking system" tid="c-call"><Input value={form.call_tracking} onChange={upd("call_tracking")} placeholder="CallRail…" className="rounded-none" data-testid="c-call-input" /></Field>
                <Field label="Requested consultation date" tid="c-date"><Input type="date" value={form.consultation_date} onChange={upd("consultation_date")} className="rounded-none" data-testid="c-date-input" /></Field>
              </div>
              <Field label="Preferred RetainProof plan" tid="c-plan">
                <Select value={form.preferred_plan} onValueChange={(v) => setForm((f) => ({ ...f, preferred_plan: v }))}>
                  <SelectTrigger className="rounded-none h-10" data-testid="c-plan-select"><SelectValue placeholder="Select a plan" /></SelectTrigger>
                  <SelectContent>{PLAN_OPTIONS.map((p) => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Current marketing-reporting challenges" tid="c-challenges">
                <Textarea value={form.reporting_challenges} onChange={upd("reporting_challenges")} rows={3} className="rounded-none" data-testid="c-challenges-input" />
              </Field>
              <Button type="submit" disabled={busy} className="w-full rounded-none bg-[#0B1F33] text-white hover:bg-zinc-800 h-11" data-testid="consultation-submit">
                {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Submit request
              </Button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[11px] uppercase tracking-widest font-mono-data text-zinc-500">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

export default ConsultationForm;
